import React, { useState } from 'react';
import * as AccessPanelModule from '../components/AccessPanel';
import * as AssetGalleryModule from '../components/AssetGallery';
import * as BrandUploaderModule from '../components/BrandUploader';
import * as FormBuilderModule from '../components/FormBuilder';
import * as ItemFormsBuilderModule from '../components/ItemFormsBuilder';
import * as LinkMetadataFormModule from '../components/LinkMetadataForm';
import * as RichContentModule from '../components/RichContent';
import * as RichContentEditorModule from '../components/RichContentEditor';
import * as ShareListTableModule from '../components/ShareListTable';
import { assetsService, getYouTubeVideoId, quickFormPresets } from '../services/assetsService';
import { storageService } from '../services/storageService';
import {
  assetsForGrid,
  amountCurrencySymbols,
  calculateFormulaValue,
  createAssetGrid,
  createListColumn,
  createListRow,
  formulaPresets,
  gridAccessLevels,
  gridCategories,
  gridTypes,
  interactionTypes,
  listColumnTypes,
  normaliseAssetGrids,
  normaliseAmountCurrencySymbol,
  normaliseFormulaPreset,
  normaliseGridAccessLevel,
  normaliseInteractionOptions,
  normaliseInteractionType,
  normaliseListColumnType,
} from '../utils/assetGrids';
import { optimiseImageFile } from '../utils/imageOptimisation';
import { defaultListingType, listingTypes } from '../utils/listingTypes';

const componentFromModule = (moduleValue) => {
  let component = moduleValue;
  while (component && typeof component === 'object' && 'default' in component) {
    component = component.default;
  }
  return component;
};

const AccessPanel = componentFromModule(AccessPanelModule);
const AssetGallery = componentFromModule(AssetGalleryModule);
const BrandUploader = componentFromModule(BrandUploaderModule);
const FormBuilder = componentFromModule(FormBuilderModule);
const ItemFormsBuilder = componentFromModule(ItemFormsBuilderModule);
const LinkMetadataForm = componentFromModule(LinkMetadataFormModule);
const RichContent = componentFromModule(RichContentModule);
const RichContentEditor = componentFromModule(RichContentEditorModule);
const ShareListTable = componentFromModule(ShareListTableModule);

const assetInputOptions = [
  { id: 'file', label: 'File upload', detail: 'Images, documents, videos, and other files' },
  { id: 'youtube', label: 'YouTube video', detail: 'Paste a YouTube URL and render a muted player' },
  { id: 'html', label: 'Embed', detail: 'Paste HTML embed code or a web link' },
  { id: 'form', label: 'Form', detail: 'Quick forms for responses, notes, and meeting capture' },
  { id: 'link-bio', label: 'Profile Cards', detail: 'Flippable cards for personal, product, service, and organisation profiles' },
  { id: 'markdown', label: 'Rich Content', detail: 'Paste Markdown, media links, and safe HTML embeds' },
];

const defaultAssetFields = {
  title: true,
  description: true,
  price: true,
  quantity: true,
  area: true,
  sizes: false,
  colors: false,
  link: false,
  multipleImages: false,
  details: true,
  enquire: false,
  share: true,
  updates: false,
};

const assetFieldOptions = [
  { id: 'title', label: 'Asset name' },
  { id: 'description', label: 'Description' },
  { id: 'price', label: 'Price' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'area', label: 'Area' },
  { id: 'sizes', label: 'Available sizes' },
  { id: 'colors', label: 'Available colors' },
  { id: 'link', label: 'Link' },
  { id: 'multipleImages', label: 'Show multiple images' },
  { id: 'details', label: 'Details button' },
  { id: 'enquire', label: 'Enquire button' },
  { id: 'share', label: 'Share button' },
  { id: 'updates', label: 'Update button' },
];

const publicPageWidthOptions = ['7xl', '6xl', '5xl', '4xl', '3xl'];
const checkoutGridCategories = ['store', 'menu'];

const defaultCheckout = {
  enableRestaurantOrdering: false,
  enableOnlineCheckout: true,
  enablePosMode: false,
  cartButtonLabel: 'Add to cart',
  drawerEyebrow: 'Store checkout',
  drawerTitle: 'Cart',
  emptyCartMessage: 'Add store items to start an order.',
  customerDetailsLabel: 'Customer details',
  formTitle: 'Send order',
  nameLabel: 'Name',
  emailLabel: 'Email',
  phoneLabel: 'Phone',
  notesLabel: 'Notes',
  addressLabel: 'Delivery address',
  tableNumberLabel: 'Table number',
  deliveryFee: '0',
  enableOfflinePaymentDetails: false,
  bankName: '',
  accountName: '',
  accountNumber: '',
  branchCode: '',
  accountType: '',
  paymentInstructions: '',
  submitLabel: 'Send order',
  successMessage: 'Order sent.',
};

const normaliseUrl = (value) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';
  if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
  return `https://${trimmedValue}`;
};

const isValidUrl = (value) => {
  try {
    const url = new URL(normaliseUrl(value));
    return ['http:', 'https:'].includes(url.protocol);
  } catch (error) {
    return false;
  }
};

const hasHtmlTag = (value = '') => /<[a-z][\s\S]*>/i.test(value.trim());

const escapeAttribute = (value = '') => value
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const createWebLinkEmbedHtml = (value = '') => {
  const url = normaliseUrl(value);
  return `<iframe src="${escapeAttribute(url)}" title="Embedded web page" loading="lazy" referrerpolicy="no-referrer-when-downgrade" style="width:100%;min-height:420px;border:0;" allow="fullscreen"></iframe>`;
};

const resolveEmbedHtml = (value = '') => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';
  return hasHtmlTag(trimmedValue) ? trimmedValue : createWebLinkEmbedHtml(trimmedValue);
};

const isValidEmbedInput = (value = '') => {
  const trimmedValue = value.trim();
  return Boolean(trimmedValue && (hasHtmlTag(trimmedValue) || isValidUrl(trimmedValue)));
};

const urlLooksLikeImage = (value = '') => /\.(avif|gif|jpe?g|png|svg|webp)(\?|#|$)/i.test(value);

const assetImageUrl = (asset = {}) => {
  const directUrl = asset.preview || asset.downloadUrl || '';
  if (asset.type === 'Image' || asset.contentType?.startsWith('image/') || urlLooksLikeImage(directUrl)) {
    return directUrl;
  }

  const galleryImage = Array.isArray(asset.images)
    ? asset.images.find((image) => image.url || image.downloadUrl || image.preview)
    : null;
  if (galleryImage) return galleryImage.url || galleryImage.downloadUrl || galleryImage.preview || '';

  if (asset.youtubeVideoId) {
    return `https://img.youtube.com/vi/${encodeURIComponent(asset.youtubeVideoId)}/hqdefault.jpg`;
  }

  return asset.iconUrl || asset.listingLogoUrl || '';
};

function GridAssetThumbnail({ asset }) {
  const thumbnailUrl = assetImageUrl(asset);
  const label = asset.title || asset.name || 'Asset';

  return (
    <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded border border-slate-200 bg-white text-xs font-semibold uppercase text-slate-400">
      {thumbnailUrl ? (
        <img
          alt={`${label} thumbnail`}
          className="h-full w-full object-cover"
          loading="lazy"
          src={thumbnailUrl}
        />
      ) : (
        <span>{String(asset.type || 'Asset').slice(0, 2)}</span>
      )}
    </span>
  );
}

function InlineTagEditor({ inputId, label, onChange, placeholder, value }) {
  const [draft, setDraft] = useState('');
  const items = Array.isArray(value) ? value : [];
  const addItem = () => {
    const nextItem = draft.trim();
    if (!nextItem) return;
    onChange([...items, nextItem]);
    setDraft('');
  };

  return (
    <div>
      <label className="field-label" htmlFor={inputId}>{label}</label>
      <div className="flex gap-2">
        <input
          className="field"
          id={inputId}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addItem();
            }
          }}
          placeholder={placeholder}
          value={draft}
        />
        <button
          className="shrink-0 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!draft.trim()}
          onClick={addItem}
          type="button"
        >
          Add
        </button>
      </div>
      {items.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {items.map((item) => (
            <span className="chip inline-flex items-center gap-2" key={item}>
              {item}
              <button
                aria-label={`Remove ${item}`}
                className="text-slate-500 transition hover:text-slate-950"
                onClick={() => onChange(items.filter((currentItem) => currentItem !== item))}
                type="button"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadIcon({ className = 'h-4 w-4' }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M12 16V4" />
      <path d="m7 9 5-5 5 5" />
      <path d="M20 16v4H4v-4" />
    </svg>
  );
}

const numericListColumnTypes = ['number', 'amount', 'percent', 'formula'];
const fileCellValue = (value) => (value && typeof value === 'object' ? value : null);

const formatFormulaValue = (value, column = {}) => {
  if (value === '') return '';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;
  return column.formula?.preset === 'percentage' ? `${numericValue.toFixed(2)}%` : numericValue.toFixed(2);
};

function FormulaModal({
  column,
  columns,
  draft,
  onClose,
  onDraftChange,
  onSave,
}) {
  if (!column) return null;

  const selectedPreset = normaliseFormulaPreset(draft.preset);
  const availableColumns = columns.filter((currentColumn) => (
    currentColumn.id !== column.id &&
    numericListColumnTypes.includes(normaliseListColumnType(currentColumn.type)) &&
    normaliseListColumnType(currentColumn.type) !== 'formula'
  ));
  const needsTwoColumns = selectedPreset !== 'sum';
  const selectedColumnIds = Array.isArray(draft.columnIds) ? draft.columnIds : [];

  const updateSelectedColumn = (index, value) => {
    const nextColumnIds = [...selectedColumnIds];
    nextColumnIds[index] = value;
    onDraftChange({
      ...draft,
      columnIds: nextColumnIds.filter(Boolean),
    });
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 px-4 py-6">
      <div className="w-full max-w-lg rounded bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="label-caps">Formula column</p>
            <h3 className="text-lg font-semibold text-slate-950">{column.label}</h3>
          </div>
          <button
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </div>

        <div className="mt-5 grid gap-4">
          <div>
            <label className="field-label mt-0" htmlFor={`formula-preset-${column.id}`}>Preset</label>
            <select
              className="field"
              id={`formula-preset-${column.id}`}
              value={selectedPreset}
              onChange={(event) => onDraftChange({
                preset: event.target.value,
                columnIds: [],
              })}
            >
              {formulaPresets.map((preset) => (
                <option key={preset.id} value={preset.id}>{preset.label}</option>
              ))}
            </select>
          </div>

          {needsTwoColumns ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {[0, 1].map((index) => (
                <div key={index}>
                  <label className="field-label mt-0" htmlFor={`formula-column-${column.id}-${index}`}>
                    {index === 0 ? 'First column' : 'Second column'}
                  </label>
                  <select
                    className="field"
                    id={`formula-column-${column.id}-${index}`}
                    value={selectedColumnIds[index] || ''}
                    onChange={(event) => updateSelectedColumn(index, event.target.value)}
                  >
                    <option value="">Select column</option>
                    {availableColumns.map((availableColumn) => (
                      <option key={availableColumn.id} value={availableColumn.id}>{availableColumn.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <p className="field-label mt-0">Columns</p>
              <div className="grid gap-2 rounded border border-slate-200 bg-slate-50 p-3">
                {availableColumns.map((availableColumn) => (
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700" key={availableColumn.id}>
                    <input
                      checked={selectedColumnIds.includes(availableColumn.id)}
                      onChange={(event) => {
                        onDraftChange({
                          ...draft,
                          columnIds: event.target.checked
                            ? [...selectedColumnIds, availableColumn.id]
                            : selectedColumnIds.filter((columnId) => columnId !== availableColumn.id),
                        });
                      }}
                      type="checkbox"
                    />
                    {availableColumn.label}
                  </label>
                ))}
                {!availableColumns.length && (
                  <p className="text-sm text-slate-500">Add a number, amount, or percent column first.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            className="rounded border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={needsTwoColumns ? selectedColumnIds.filter(Boolean).length < 2 : selectedColumnIds.length < 1}
            onClick={onSave}
            type="button"
          >
            Save formula
          </button>
        </div>
      </div>
    </div>
  );
}

function InteractionOptionsInput({ column, onCommit }) {
  const [draft, setDraft] = useState(normaliseInteractionOptions(column.interaction?.options).join(', '));

  const commitOptions = () => {
    onCommit(normaliseInteractionOptions(draft));
  };

  return (
    <input
      className="field"
      onBlur={commitOptions}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commitOptions();
        }
      }}
      placeholder="Options: Yes, No, Maybe"
      value={draft}
    />
  );
}

function LinkCreationPage({
  link,
  error,
  isFreePlan = false,
  isUploadingAssets,
  isSaving,
  isSignedIn,
  uploadLimitBytes,
  uploadUsedBytes = 0,
  onChange,
  onSave,
  onUploadAssets,
  onOpenLiveShare,
  onAddManualAsset,
  onAssetChange,
  onAssetDelete,
  onAssetMove,
}) {
  const [activeView, setActiveView] = useState('link');
  const [activeCreatorPanel, setActiveCreatorPanel] = useState('link');
  const [assetInputMode, setAssetInputMode] = useState('file');
  const [saveStatus, setSaveStatus] = useState('');
  const [embedTitle, setEmbedTitle] = useState('');
  const [embedHtml, setEmbedHtml] = useState('');
  const [youtubeTitle, setYouTubeTitle] = useState('');
  const [youtubeUrl, setYouTubeUrl] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPresetId, setFormPresetId] = useState('meeting');
  const [linkBioTitle, setLinkBioTitle] = useState('');
  const [linkBioTagline, setLinkBioTagline] = useState('');
  const [linkBioDescription, setLinkBioDescription] = useState('');
  const [linkBioQualifications, setLinkBioQualifications] = useState([]);
  const [linkBioExperience, setLinkBioExperience] = useState('');
  const [linkBioTags, setLinkBioTags] = useState([]);
  const [linkBioUrl, setLinkBioUrl] = useState('');
  const [linkBioCtaLabel, setLinkBioCtaLabel] = useState('Open profile');
  const [linkBioSection, setLinkBioSection] = useState('Personal');
  const [linkBioIconFile, setLinkBioIconFile] = useState(null);
  const [linkBioIconPreview, setLinkBioIconPreview] = useState('');
  const [linkBioIsPrimary, setLinkBioIsPrimary] = useState(false);
  const [articleTitle, setArticleTitle] = useState('');
  const [articleMarkdown, setArticleMarkdown] = useState('');
  const [uploadPreparationError, setUploadPreparationError] = useState('');
  const [uploadPreparationStatus, setUploadPreparationStatus] = useState('');
  const [activeGridId, setActiveGridId] = useState('');
  const [formulaColumnId, setFormulaColumnId] = useState('');
  const [formulaDraft, setFormulaDraft] = useState({ preset: 'sum', columnIds: [] });
  const [listUploadStatus, setListUploadStatus] = useState('');
  const [listUploadError, setListUploadError] = useState('');
  const assetFields = { ...defaultAssetFields, ...(link?.assetFields || {}) };
  const assetGrids = normaliseAssetGrids(link || {});
  const activeGrid = assetGrids.find((grid) => grid.id === activeGridId) || assetGrids[0];
  const activeGridAssets = assetsForGrid(link?.assets || [], activeGrid);
  const activeGridColumns = Math.min(Math.max(Number(activeGrid?.columns) || 3, 1), 4);
  const checkout = { ...defaultCheckout, ...(link?.checkout || {}), gridId: '' };
  const canUploadAssets = Boolean(link?.id);
  const isUploading = Boolean(isUploadingAssets && link?.id);
  const uploadActivityLabel = 'Optimising and uploading images...';
  const formatUploadSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  const canAddEmbed = isValidEmbedInput(embedHtml);
  const isEmbedInputWebLink = Boolean(embedHtml.trim() && !hasHtmlTag(embedHtml) && isValidUrl(embedHtml));

  const handleUploadFiles = async (files) => {
    if (!canUploadAssets || isUploading) return;
    if (!files?.length) return;

    setUploadPreparationError('');
    setUploadPreparationStatus('');
    try {
      setUploadPreparationStatus('Images will be converted to JPG and compressed during upload.');

      await Promise.resolve(onUploadAssets(
        { target: { files: Array.from(files || []), value: '' } },
        { gridId: activeGrid?.id },
      ));
    } catch (error) {
      setUploadPreparationError(error.message || 'Unable to prepare files for upload.');
    }
  };

  const handleAssetDrop = (event) => {
    event.preventDefault();
    handleUploadFiles(Array.from(event.dataTransfer.files || []));
  };

  const handleAssetUploadChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    handleUploadFiles(files);
  };

  const handleSaveClick = async () => {
    setSaveStatus('');
    const wasSaved = await onSave?.();
    if (wasSaved) {
      setSaveStatus(activeView === 'assets' ? 'Assets saved successfully.' : 'Link saved successfully.');
    }
  };

  const handleAddEmbed = () => {
    if (!canAddEmbed) return;
    onAddManualAsset?.(assetsService.createHtmlEmbed({
      title: embedTitle,
      embedHtml: resolveEmbedHtml(embedHtml),
    }), { gridId: activeGrid?.id });
    setEmbedTitle('');
    setEmbedHtml('');
  };

  const handleAddYouTubeVideo = () => {
    if (!getYouTubeVideoId(youtubeUrl)) return;
    onAddManualAsset?.(assetsService.createYouTubeVideo({
      title: youtubeTitle,
      youtubeUrl,
    }), { gridId: activeGrid?.id });
    setYouTubeTitle('');
    setYouTubeUrl('');
  };

  const handleAddForm = () => {
    onAddManualAsset?.(assetsService.createForm({
      title: formTitle,
      description: formDescription,
      presetId: formPresetId,
    }), { gridId: activeGrid?.id });
    setFormTitle('');
    setFormDescription('');
    setFormPresetId('meeting');
  };

  const handleAddLinkBio = () => {
    if (!isValidUrl(linkBioUrl)) return;
    onAddManualAsset?.(assetsService.createProfileCard({
      title: linkBioTitle,
      description: linkBioDescription,
      tagline: linkBioTagline,
      qualifications: linkBioQualifications,
      experience: linkBioExperience,
      tags: linkBioTags,
      externalUrl: normaliseUrl(linkBioUrl),
      ctaLabel: linkBioCtaLabel,
      section: linkBioSection,
      iconUrl: linkBioIconPreview,
      iconFile: linkBioIconFile,
      isPrimaryAction: linkBioIsPrimary,
    }), { gridId: activeGrid?.id });
    setLinkBioTitle('');
    setLinkBioTagline('');
    setLinkBioDescription('');
    setLinkBioQualifications([]);
    setLinkBioExperience('');
    setLinkBioTags([]);
    setLinkBioUrl('');
    setLinkBioCtaLabel('Open profile');
    setLinkBioSection('Personal');
    setLinkBioIconFile(null);
    setLinkBioIconPreview('');
    setLinkBioIsPrimary(false);
  };

  const handleAddMarkdownArticle = () => {
    if (!articleMarkdown.trim()) return;
    onAddManualAsset?.(assetsService.createMarkdownArticle({
      title: articleTitle,
      articleMarkdown,
    }), { gridId: activeGrid?.id });
    setArticleTitle('');
    setArticleMarkdown('');
  };

  const uploadRichContentImage = async (file) => {
    if (!link?.id) {
      throw new Error('Create the link before uploading rich content images.');
    }

    const uploadFile = await optimiseImageFile(file);
    const upload = await storageService.uploadAssetFile(link.id, uploadFile);
    return upload.downloadUrl;
  };

  const updateAssetGrids = (nextGrids) => {
    onChange({
      ...link,
      assetGrids: nextGrids,
    });
  };

  const handleAddGrid = () => {
    const nextGrid = {
      ...createAssetGrid(`Grid ${assetGrids.length + 1}`, []),
      category: link.category || 'album',
      layout: link.layout || 'masonry',
      columns: link.galleryColumns || 3,
    };
    updateAssetGrids([...assetGrids, nextGrid]);
    setActiveGridId(nextGrid.id);
  };

  const handleDeleteGrid = (gridId) => {
    if (assetGrids.length <= 1) return;
    const nextGrids = assetGrids.filter((grid) => grid.id !== gridId);
    updateAssetGrids(nextGrids);
    setActiveGridId(nextGrids[0]?.id || '');
  };

  const handleGridTitleChange = (gridId, title) => {
    updateAssetGrids(assetGrids.map((grid) => (
      grid.id === gridId ? { ...grid, title } : grid
    )));
  };

  const handleGridTypeChange = (gridId, type) => {
    updateAssetGrids(assetGrids.map((grid) => (
      grid.id === gridId ? { ...grid, type } : grid
    )));
  };

  const handleGridFieldChange = (gridId, field, value) => {
    updateAssetGrids(assetGrids.map((grid) => (
      grid.id === gridId ? {
        ...grid,
        [field]: value,
        ...(field === 'category' && value === 'listing' && !grid.listingType ? { listingType: defaultListingType } : {}),
      } : grid
    )));
  };

  const updateGridList = (gridId, updater) => {
    updateAssetGrids(assetGrids.map((grid) => (
      grid.id === gridId ? updater(grid) : grid
    )));
  };

  const handleAddListColumn = (gridId) => {
    updateGridList(gridId, (grid) => {
      const nextColumn = createListColumn(`Column ${(grid.listColumns || []).length + 1}`);
      return {
        ...grid,
        listColumns: [...(grid.listColumns || []), nextColumn],
        listRows: (grid.listRows || []).map((row) => ({
          ...row,
          cells: {
            ...(row.cells || {}),
            [nextColumn.id]: '',
          },
        })),
      };
    });
  };

  const handleRemoveListColumn = (gridId, columnId) => {
    updateGridList(gridId, (grid) => ({
      ...grid,
      listColumns: (grid.listColumns || []).filter((column) => column.id !== columnId),
      listRows: (grid.listRows || []).map((row) => {
        const { [columnId]: removedValue, ...nextCells } = row.cells || {};
        return { ...row, cells: nextCells };
      }),
    }));
  };

  const handleListColumnTypeChange = (gridId, columnId, type) => {
    const nextType = normaliseListColumnType(type);
    updateGridList(gridId, (grid) => ({
      ...grid,
      listColumns: (grid.listColumns || []).map((column) => (
        column.id === columnId
          ? {
            ...column,
            type: nextType,
            formula: nextType === 'formula'
              ? column.formula || { preset: 'sum', columnIds: [] }
              : null,
            interaction: nextType === 'interaction'
              ? column.interaction || { type: 'text', options: [] }
              : null,
          }
          : column
      )),
      listRows: (grid.listRows || []).map((row) => ({
        ...row,
        cells: {
          ...(row.cells || {}),
          [columnId]: nextType === 'formula' ? '' : row.cells?.[columnId] || '',
        },
      })),
    }));
  };

  const handleListColumnFieldChange = (gridId, columnId, field, value) => {
    updateGridList(gridId, (grid) => ({
      ...grid,
      listColumns: (grid.listColumns || []).map((column) => (
        column.id === columnId
          ? {
            ...column,
            [field]: field === 'currencySymbol' ? normaliseAmountCurrencySymbol(value) : value,
          }
          : column
      )),
    }));
  };

  const openFormulaModal = (column) => {
    setFormulaColumnId(column.id);
    setFormulaDraft(column.formula || { preset: 'sum', columnIds: [] });
  };

  const saveFormulaColumn = () => {
    if (!activeGrid?.id || !formulaColumnId) return;
    updateGridList(activeGrid.id, (grid) => ({
      ...grid,
      listColumns: (grid.listColumns || []).map((column) => (
        column.id === formulaColumnId
          ? {
            ...column,
            type: 'formula',
            formula: {
              preset: normaliseFormulaPreset(formulaDraft.preset),
              columnIds: Array.isArray(formulaDraft.columnIds)
                ? formulaDraft.columnIds.filter(Boolean)
                : [],
            },
          }
          : column
      )),
    }));
    setFormulaColumnId('');
    setFormulaDraft({ preset: 'sum', columnIds: [] });
  };

  const handleAddListRow = (gridId) => {
    updateGridList(gridId, (grid) => ({
      ...grid,
      listRows: [...(grid.listRows || []), createListRow(grid.listColumns || [])],
    }));
  };

  const handleRemoveListRow = (gridId, rowId) => {
    updateGridList(gridId, (grid) => ({
      ...grid,
      listRows: (grid.listRows || []).filter((row) => row.id !== rowId),
    }));
  };

  const handleListCellChange = (gridId, rowId, columnId, value) => {
    updateGridList(gridId, (grid) => ({
      ...grid,
      listRows: (grid.listRows || []).map((row) => (
        row.id === rowId
          ? { ...row, cells: { ...(row.cells || {}), [columnId]: value } }
          : row
      )),
    }));
  };

  const handleListCellFileUpload = async (gridId, rowId, column, file) => {
    if (!file) return;
    if (!link?.id) {
      setListUploadError('Save the link before uploading list files.');
      return;
    }

    setListUploadError('');
    setListUploadStatus(`Uploading ${file.name}...`);
    try {
      const uploadFile = column.type === 'image' ? await optimiseImageFile(file) : file;
      const upload = await storageService.uploadAssetFile(link.id, uploadFile);
      handleListCellChange(gridId, rowId, column.id, {
        name: file.name,
        downloadUrl: upload.downloadUrl,
        contentType: upload.contentType || file.type,
        size: upload.size || file.size || 0,
      });
      setListUploadStatus('List file uploaded.');
    } catch (error) {
      setListUploadError(error.message || 'Unable to upload list file.');
    }
  };

  const handleListRowFieldChange = (gridId, rowId, field, value) => {
    updateGridList(gridId, (grid) => ({
      ...grid,
      listRows: (grid.listRows || []).map((row) => (
        row.id === rowId ? { ...row, [field]: value } : row
      )),
    }));
  };

  const handleGridAssetToggle = (gridId, assetId, isSelected) => {
    updateAssetGrids(assetGrids.map((grid) => {
      if (grid.id !== gridId) return grid;
      const currentAssetIds = grid.assetIds || [];

      return {
        ...grid,
        assetIds: isSelected
          ? [...new Set([...currentAssetIds, assetId])]
          : currentAssetIds.filter((currentAssetId) => currentAssetId !== assetId),
      };
    }));
  };

  const handleActiveGridAssetMove = (fromIndex, toIndex) => {
    const fromAssetId = activeGridAssets[fromIndex]?.id;
    const toAssetId = activeGridAssets[toIndex]?.id;
    const fromGlobalIndex = (link.assets || []).findIndex((asset) => asset.id === fromAssetId);
    const toGlobalIndex = (link.assets || []).findIndex((asset) => asset.id === toAssetId);

    onAssetMove?.(fromGlobalIndex, toGlobalIndex);
  };

  const activeFormulaColumn = activeGrid?.listColumns?.find((column) => column.id === formulaColumnId);

  const renderListEditorCell = (row, column) => {
    const columnType = normaliseListColumnType(column.type);
    const value = row.cells?.[column.id] || '';
    const inputValue = value && typeof value === 'object' ? value.name || '' : String(value ?? '');

    if (columnType === 'formula') {
      return (
        <input
          className="field"
          readOnly
          value={formatFormulaValue(calculateFormulaValue(row, column), column)}
        />
      );
    }

    if (columnType === 'image' || columnType === 'file') {
      const uploadedFile = fileCellValue(value);
      const fileUrl = uploadedFile?.downloadUrl || '';
      const uploadInputId = `list-upload-${activeGrid.id}-${row.id}-${column.id}`;
      return (
        <div className="grid min-w-52 gap-2">
          {columnType === 'image' && fileUrl && (
            <img className="h-16 w-24 rounded border border-slate-200 object-cover" src={fileUrl} alt={uploadedFile.name || column.label} />
          )}
          {columnType === 'file' && fileUrl && (
            <a className="truncate text-sm font-semibold text-slate-700 underline" href={fileUrl} rel="noreferrer" target="_blank">
              {uploadedFile.name || 'Open file'}
            </a>
          )}
          <label
            aria-label={`${fileUrl ? 'Replace' : 'Upload'} ${column.label}`}
            className={`inline-grid h-10 w-10 cursor-pointer place-items-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:bg-slate-50 hover:text-slate-950 ${!link?.id ? 'pointer-events-none opacity-50' : ''}`}
            htmlFor={uploadInputId}
            title={fileUrl ? 'Replace file' : 'Upload file'}
          >
            <UploadIcon />
          </label>
          <input
            accept={columnType === 'image' ? 'image/*' : undefined}
            className="sr-only"
            disabled={!link?.id}
            id={uploadInputId}
            onChange={(event) => {
              const [file] = event.target.files || [];
              event.target.value = '';
              handleListCellFileUpload(activeGrid.id, row.id, column, file);
            }}
            type="file"
          />
        </div>
      );
    }

    if (columnType === 'link') {
      return (
        <input
          className="field"
          placeholder="https://..."
          value={inputValue}
          onChange={(event) => handleListCellChange(activeGrid.id, row.id, column.id, event.target.value)}
        />
      );
    }

    if (columnType === 'interaction') {
      const interactionType = normaliseInteractionType(column.interaction?.type);
      const options = normaliseInteractionOptions(column.interaction?.options);

      if (interactionType === 'buttons') {
        return (
          <div className="flex min-w-48 flex-wrap gap-2">
            {(options.length ? options : ['Option']).map((option) => (
              <button
                className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                disabled
                key={option}
                type="button"
              >
                {option}
              </button>
            ))}
          </div>
        );
      }

      if (interactionType === 'select') {
        return (
          <select className="field min-w-40" disabled value="">
            <option value="">Select</option>
            {options.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      }

      if (interactionType === 'check') {
        return <input disabled type="checkbox" />;
      }

      if (interactionType === 'rating') {
        return (
          <div className="flex min-w-32 gap-1 text-xl leading-none text-amber-400">
            {[1, 2, 3, 4, 5].map((rating) => <span key={rating}>★</span>)}
          </div>
        );
      }

      return <input className="field min-w-48" disabled placeholder="Text response" />;
    }

    return (
      <input
        className="field"
        type={columnType === 'date' ? 'date' : ['number', 'amount', 'percent'].includes(columnType) ? 'number' : 'text'}
        step={['amount', 'percent'].includes(columnType) ? '0.01' : undefined}
        value={inputValue}
        onChange={(event) => handleListCellChange(activeGrid.id, row.id, column.id, event.target.value)}
      />
    );
  };

  if (!link) {
    return (
      <section className="mx-auto max-w-5xl px-5 py-6 sm:px-8">
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-sm text-slate-600">
          {isSignedIn
            ? 'Preparing a new asset link...'
            : 'Sign in, then create a new asset link from the library.'}
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto grid max-w-5xl gap-6 px-5 py-6 sm:px-8">
      <div aria-label="Create Link views" className="inline-flex w-fit max-w-full items-center justify-self-start rounded-full bg-slate-200/70 p-1" role="group">
        {[
          { id: 'link', label: 'Link creation' },
          { id: 'assets', label: 'Upload assets' },
        ].map((view) => (
          <button
            aria-controls={`creator-view-${view.id}`}
            aria-pressed={activeView === view.id}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 ${
              activeView === view.id
                ? 'bg-white text-slate-950 shadow-sm'
                : 'text-slate-600 hover:bg-white/50 hover:text-slate-950'
            }`}
            key={view.id}
            onClick={() => {
              setActiveView(view.id);
              setSaveStatus('');
            }}
            type="button"
          >
            {view.label}
          </button>
        ))}
      </div>
      {!isSignedIn && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Sign in before saving this link to Firestore.
        </div>
      )}
      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}
      {saveStatus && !error && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700" role="status">
          {saveStatus}
        </div>
      )}
      <section className={`creator-view ${activeView === 'link' ? 'space-y-4' : 'hidden'}`} hidden={activeView !== 'link'} id="creator-view-link">
        <div className="grid grid-cols-2 rounded-lg border border-slate-200 bg-white p-1 sm:grid-cols-4">
          <button
            aria-pressed={activeCreatorPanel === 'link'}
            className={activeCreatorPanel === 'link' ? 'segmented active' : 'segmented'}
            onClick={() => setActiveCreatorPanel('link')}
            type="button"
          >
            Link settings
          </button>
          <button
            aria-pressed={activeCreatorPanel === 'form'}
            className={activeCreatorPanel === 'form' ? 'segmented active' : 'segmented'}
            onClick={() => setActiveCreatorPanel('form')}
            type="button"
          >
            Response form
          </button>
          <button
            aria-pressed={activeCreatorPanel === 'checkout'}
            className={activeCreatorPanel === 'checkout' ? 'segmented active' : 'segmented'}
            onClick={() => setActiveCreatorPanel('checkout')}
            type="button"
          >
            Checkout
          </button>
          <button
            aria-pressed={activeCreatorPanel === 'itemForms'}
            className={activeCreatorPanel === 'itemForms' ? 'segmented active' : 'segmented'}
            onClick={() => setActiveCreatorPanel('itemForms')}
            type="button"
          >
            Item forms
          </button>
        </div>

        {activeCreatorPanel === 'link' && (
          <>
            <LinkMetadataForm link={link} onChange={onChange} />
            <BrandUploader
              disabled={isFreePlan}
              icon={link.icon}
              logo={link.logo}
              logoHeight={link.logoHeight}
              ogImage={link.ogImage}
              onImageChange={(field, file, previewUrl) => onChange({
                ...link,
                [field]: previewUrl,
                ...(!file && !previewUrl ? { [`${field}UploadBytes`]: 0 } : {}),
              }, { field, file })}
              onLogoHeightChange={(logoHeight) => onChange({ ...link, logoHeight })}
            />
            <AccessPanel
              isFreePlan={isFreePlan}
              link={link}
              onChange={onChange}
            />
            {isFreePlan && (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Free plan: one link, expires after 14 days, {formatUploadSize(uploadUsedBytes)} of {formatUploadSize(uploadLimitBytes)} uploaded.
              </div>
            )}
          </>
        )}

        {activeCreatorPanel === 'form' && (
          <FormBuilder
            form={{ ...(link.form || {}), gridId: link.form?.gridId || activeGrid?.id || '' }}
            linkItems={activeGridAssets}
            onChange={(form) => onChange({ ...link, form: { ...form, gridId: activeGrid?.id || form.gridId || '' } })}
          />
        )}

        {activeCreatorPanel === 'checkout' && (
          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <div>
              <p className="label-caps">Checkout form</p>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Customize the cart drawer and the button shown on store or menu items.
              </p>
            </div>

            <label className="mt-5 flex items-start gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <input
                checked={Boolean(checkout.enablePosMode)}
                className="mt-1"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, enablePosMode: event.target.checked },
                })}
                type="checkbox"
              />
              <span>
                <span className="block font-semibold text-slate-950">Enable in-store POS mode</span>
                <span className="mt-1 block leading-6">
                  Signed-in owners can record in-store sales from this checkout link with payment type instead of customer details.
                </span>
              </span>
            </label>

            <label className="mt-3 flex items-start gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <input
                checked={checkout.enableOnlineCheckout !== false}
                className="mt-1"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, enableOnlineCheckout: event.target.checked },
                })}
                type="checkbox"
              />
              <span>
                <span className="block font-semibold text-slate-950">Enable customer online ordering</span>
                <span className="mt-1 block leading-6">
                  Turn this off when customers should not place orders from the public page. If POS mode is enabled, signed-in owners can still record in-store sales.
                </span>
              </span>
            </label>

            <label className="mt-3 flex items-start gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <input
                checked={Boolean(checkout.enableRestaurantOrdering)}
                className="mt-1"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, enableRestaurantOrdering: event.target.checked },
                })}
                type="checkbox"
              />
              <span>
                <span className="block font-semibold text-slate-950">Enable onsite/offsite restaurant ordering</span>
                <span className="mt-1 block leading-6">
                  Public checkout shows onsite table orders and offsite delivery orders. Leave this off for the standard online checkout.
                </span>
              </span>
            </label>

            <label className="field-label" htmlFor="checkout-cart-button-label">Add to cart button</label>
            <input
              className="field"
              id="checkout-cart-button-label"
              onChange={(event) => onChange({
                ...link,
                checkout: { ...checkout, cartButtonLabel: event.target.value },
              })}
              value={checkout.cartButtonLabel}
            />

            <div className="mt-5 border-t border-slate-200 pt-5">
              <p className="text-sm font-semibold text-slate-950">Cart drawer</p>
              <label className="field-label" htmlFor="checkout-drawer-eyebrow">Eyebrow</label>
              <input
                className="field"
                id="checkout-drawer-eyebrow"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, drawerEyebrow: event.target.value },
                })}
                value={checkout.drawerEyebrow}
              />

              <label className="field-label" htmlFor="checkout-drawer-title">Title</label>
              <input
                className="field"
                id="checkout-drawer-title"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, drawerTitle: event.target.value },
                })}
                value={checkout.drawerTitle}
              />

              <label className="field-label" htmlFor="checkout-empty-cart-message">Empty cart message</label>
              <textarea
                className="field min-h-20 resize-none"
                id="checkout-empty-cart-message"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, emptyCartMessage: event.target.value },
                })}
                value={checkout.emptyCartMessage}
              />
            </div>

            <div className="mt-5 border-t border-slate-200 pt-5">
              <p className="text-sm font-semibold text-slate-950">Customer form</p>
              <label className="field-label" htmlFor="checkout-customer-details-label">Section label</label>
              <input
                className="field"
                id="checkout-customer-details-label"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, customerDetailsLabel: event.target.value },
                })}
                value={checkout.customerDetailsLabel}
              />

              <label className="field-label" htmlFor="checkout-form-title">Form title</label>
              <input
                className="field"
                id="checkout-form-title"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, formTitle: event.target.value },
                })}
                value={checkout.formTitle}
              />

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="checkout-name-label">Name label</label>
                  <input
                    className="field"
                    id="checkout-name-label"
                    onChange={(event) => onChange({
                      ...link,
                      checkout: { ...checkout, nameLabel: event.target.value },
                    })}
                    value={checkout.nameLabel}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="checkout-email-label">Email label</label>
                  <input
                    className="field"
                    id="checkout-email-label"
                    onChange={(event) => onChange({
                      ...link,
                      checkout: { ...checkout, emailLabel: event.target.value },
                    })}
                    value={checkout.emailLabel}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="checkout-phone-label">Phone label</label>
                  <input
                    className="field"
                    id="checkout-phone-label"
                    onChange={(event) => onChange({
                      ...link,
                      checkout: { ...checkout, phoneLabel: event.target.value },
                    })}
                    value={checkout.phoneLabel}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="checkout-notes-label">Notes label</label>
                  <input
                    className="field"
                    id="checkout-notes-label"
                    onChange={(event) => onChange({
                      ...link,
                      checkout: { ...checkout, notesLabel: event.target.value },
                    })}
                    value={checkout.notesLabel}
                  />
                </div>
                {checkout.enableRestaurantOrdering && (
                  <>
                    <div>
                      <label className="field-label" htmlFor="checkout-address-label">Delivery address label</label>
                      <input
                        className="field"
                        id="checkout-address-label"
                        onChange={(event) => onChange({
                          ...link,
                          checkout: { ...checkout, addressLabel: event.target.value },
                        })}
                        value={checkout.addressLabel}
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="checkout-table-number-label">Table number label</label>
                      <input
                        className="field"
                        id="checkout-table-number-label"
                        onChange={(event) => onChange({
                          ...link,
                          checkout: { ...checkout, tableNumberLabel: event.target.value },
                        })}
                        value={checkout.tableNumberLabel}
                      />
                    </div>
                  </>
                )}
              </div>

              {checkout.enableRestaurantOrdering && (
                <>
                  <label className="field-label" htmlFor="checkout-delivery-fee">Offsite delivery fee</label>
                  <input
                    className="field"
                    id="checkout-delivery-fee"
                    min="0"
                    onChange={(event) => onChange({
                      ...link,
                      checkout: { ...checkout, deliveryFee: event.target.value },
                    })}
                    type="number"
                    value={checkout.deliveryFee}
                  />
                </>
              )}

              <label className="field-label" htmlFor="checkout-submit-label">Submit button</label>
              <input
                className="field"
                id="checkout-submit-label"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, submitLabel: event.target.value },
                })}
                value={checkout.submitLabel}
              />

              <label className="field-label" htmlFor="checkout-success-message">Success message</label>
              <input
                className="field"
                id="checkout-success-message"
                onChange={(event) => onChange({
                  ...link,
                  checkout: { ...checkout, successMessage: event.target.value },
                })}
                value={checkout.successMessage}
              />

              <label className="mt-5 flex items-start gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <input
                  checked={Boolean(checkout.enableOfflinePaymentDetails)}
                  className="mt-1"
                  onChange={(event) => onChange({
                    ...link,
                    checkout: { ...checkout, enableOfflinePaymentDetails: event.target.checked },
                  })}
                  type="checkbox"
                />
                <span>
                  <span className="block font-semibold text-slate-950">Show banking details after order submission</span>
                  <span className="mt-1 block leading-6">
                    Customers will see these offline payment details after a successful order. The order number is shown as the payment reference.
                  </span>
                </span>
              </label>

              {checkout.enableOfflinePaymentDetails && (
                <div className="mt-4 grid gap-3 rounded border border-slate-200 bg-slate-50 p-3">
                  <label className="field-label mt-0" htmlFor="checkout-bank-name">Bank name</label>
                  <input
                    className="field"
                    id="checkout-bank-name"
                    onChange={(event) => onChange({
                      ...link,
                      checkout: { ...checkout, bankName: event.target.value },
                    })}
                    value={checkout.bankName}
                  />

                  <label className="field-label" htmlFor="checkout-account-name">Account name</label>
                  <input
                    className="field"
                    id="checkout-account-name"
                    onChange={(event) => onChange({
                      ...link,
                      checkout: { ...checkout, accountName: event.target.value },
                    })}
                    value={checkout.accountName}
                  />

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="field-label" htmlFor="checkout-account-number">Account number</label>
                      <input
                        className="field"
                        id="checkout-account-number"
                        onChange={(event) => onChange({
                          ...link,
                          checkout: { ...checkout, accountNumber: event.target.value },
                        })}
                        value={checkout.accountNumber}
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="checkout-branch-code">Branch code</label>
                      <input
                        className="field"
                        id="checkout-branch-code"
                        onChange={(event) => onChange({
                          ...link,
                          checkout: { ...checkout, branchCode: event.target.value },
                        })}
                        value={checkout.branchCode}
                      />
                    </div>
                  </div>

                  <label className="field-label" htmlFor="checkout-account-type">Account type</label>
                  <input
                    className="field"
                    id="checkout-account-type"
                    onChange={(event) => onChange({
                      ...link,
                      checkout: { ...checkout, accountType: event.target.value },
                    })}
                    value={checkout.accountType}
                  />

                  <label className="field-label" htmlFor="checkout-payment-instructions">Payment instructions</label>
                  <textarea
                    className="field min-h-24 resize-none"
                    id="checkout-payment-instructions"
                    onChange={(event) => onChange({
                      ...link,
                      checkout: { ...checkout, paymentInstructions: event.target.value },
                    })}
                    placeholder="Use your order number as the payment reference."
                    value={checkout.paymentInstructions}
                  />
                </div>
              )}
            </div>
          </section>
        )}

        {activeCreatorPanel === 'itemForms' && (
          <ItemFormsBuilder
            activeGridId={activeGrid?.id || ''}
            forms={link.itemForms || []}
            onChange={(itemForms) => onChange({ ...link, itemForms })}
          />
        )}

        <button
          className="w-full rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
          disabled={!isSignedIn || isSaving || isUploadingAssets}
          onClick={handleSaveClick}
          type="button"
        >
          {isSaving ? 'Saving...' : link.id ? 'Save link' : 'Create link'}
        </button>
      </section>

      <section className={`creator-view ${activeView === 'assets' ? 'min-w-0 space-y-5' : 'hidden'}`} hidden={activeView !== 'assets'} id="creator-view-assets">
        <section className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="label-caps mb-2">Assets to share</p>
              <h2 className="text-xl font-semibold text-slate-950">Add content for this link</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                Choose one asset source, then add the content that should appear in the public share experience.
              </p>
            </div>
            <div className="grid grid-cols-3 overflow-hidden rounded border border-slate-200 bg-slate-50 text-right">
              <div className="border-r border-slate-200 px-3 py-2">
                <span className="block text-2xl font-semibold text-slate-950">{link.assets?.length || 0}</span>
                <span className="text-xs font-semibold uppercase text-slate-500">Assets</span>
              </div>
              <div className="border-r border-slate-200 px-3 py-2">
                <span className="block text-2xl font-semibold text-slate-950">{link.views || 0}</span>
                <span className="text-xs font-semibold uppercase text-slate-500">Views</span>
              </div>
              <div className="px-3 py-2">
                <span className="block text-2xl font-semibold text-slate-950">{link.shares || 0}</span>
                <span className="text-xs font-semibold uppercase text-slate-500">Shares</span>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3 md:grid-cols-6">
            {assetInputOptions.map((option) => (
              <button
                aria-pressed={assetInputMode === option.id}
                className={`asset-source-option ${assetInputMode === option.id ? 'active' : ''}`}
                key={option.id}
                onClick={() => setAssetInputMode(option.id)}
                type="button"
              >
                <span className="text-sm font-semibold text-slate-950">{option.label}</span>
                <span className="mt-1 block text-xs leading-5 text-slate-500">{option.detail}</span>
              </button>
            ))}
          </div>

          {assetInputMode === 'file' && (
            <>
              <p className="mt-5 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
                Images are converted to JPG and compressed to 400 KB before upload.
              </p>
              <label
                className={`asset-upload-zone group relative mt-3 ${canUploadAssets && !isUploading ? '' : 'cursor-not-allowed opacity-60 hover:border-slate-300 hover:bg-slate-50'}`}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleAssetDrop}
              >
                {isUploading && (
                  <span className="absolute inset-0 z-10 grid place-items-center rounded-lg bg-white/90 text-sm font-semibold text-slate-700">
                    {uploadActivityLabel}
                  </span>
                )}
                <input type="file" multiple onChange={handleAssetUploadChange} disabled={!canUploadAssets || isUploading} />
                <span className="grid h-12 w-12 place-items-center rounded bg-slate-950 text-2xl font-light text-white">+</span>
                <span className="min-w-0">
                  <span className="block text-base font-semibold text-slate-950">Choose files or drop them here</span>
                  <span className="mt-1 block text-sm text-slate-500">
                    {isUploading
                      ? uploadActivityLabel
                      : canUploadAssets ? 'Multiple files can be uploaded at once.' : 'Create the link before uploading assets.'}
                  </span>
                </span>
                <span className="asset-upload-action">Browse files</span>
              </label>
              {uploadPreparationError && (
                <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {uploadPreparationError}
                </p>
              )}
              {uploadPreparationStatus && !uploadPreparationError && (
                <p className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {uploadPreparationStatus}
                </p>
              )}
            </>
          )}

          {assetInputMode === 'youtube' && (
            <div className="mt-5 border-t border-slate-200 pt-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-950">YouTube video</p>
                  <p className="mt-1 max-w-xl text-sm leading-6 text-slate-500">
                    Paste a YouTube watch, Shorts, embed, or youtu.be URL. The public page renders it muted with video controls.
                  </p>
                </div>
                <button
                  className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!getYouTubeVideoId(youtubeUrl)}
                  onClick={handleAddYouTubeVideo}
                  type="button"
                >
                  Add video
                </button>
              </div>
              <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,16rem)_1fr]">
                <div>
                  <label className="field-label mt-0" htmlFor="youtube-video-title">Title</label>
                  <input
                    id="youtube-video-title"
                    className="field"
                    placeholder="Product walkthrough"
                    value={youtubeTitle}
                    onChange={(event) => setYouTubeTitle(event.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label mt-0" htmlFor="youtube-video-url">YouTube URL</label>
                  <input
                    id="youtube-video-url"
                    className="field"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={youtubeUrl}
                    onChange={(event) => setYouTubeUrl(event.target.value)}
                  />
                  {youtubeUrl && !getYouTubeVideoId(youtubeUrl) && (
                    <p className="mt-2 text-xs font-semibold text-red-600">
                      Paste a valid YouTube URL.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {assetInputMode === 'html' && (
            <div className="mt-5 border-t border-slate-200 pt-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-950">Embed</p>
                  <p className="mt-1 max-w-xl text-sm leading-6 text-slate-500">
                    Add pasted HTML embed code or embed a web page from its URL.
                  </p>
                </div>
                <button
                  className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!canAddEmbed}
                  onClick={handleAddEmbed}
                  type="button"
                >
                  Add embed
                </button>
              </div>
              <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,16rem)_1fr]">
                <div>
                  <label className="field-label mt-0" htmlFor="html-embed-title">Title</label>
                  <input
                    id="html-embed-title"
                    className="field"
                    placeholder="Interactive demo"
                    value={embedTitle}
                    onChange={(event) => setEmbedTitle(event.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label mt-0" htmlFor="html-embed-code">HTML or web link</label>
                  <textarea
                    id="html-embed-code"
                    className="field min-h-28 font-mono text-xs"
                    placeholder={'https://example.com\n\nor\n\n<iframe src="https://..."></iframe>'}
                    value={embedHtml}
                    onChange={(event) => setEmbedHtml(event.target.value)}
                  />
                  {isEmbedInputWebLink && (
                    <p className="mt-2 text-xs font-semibold text-slate-500">
                      This web link will be embedded in an iframe. Some sites may block iframe display.
                    </p>
                  )}
                  {embedHtml.trim() && !canAddEmbed && (
                    <p className="mt-2 text-xs font-semibold text-red-600">
                      Paste valid HTML embed code or a valid http/https web link.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {assetInputMode === 'form' && (
            <div className="mt-5 border-t border-slate-200 pt-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-950">Quick form</p>
                  <p className="mt-1 max-w-xl text-sm leading-6 text-slate-500">
                    Add an inline form to this share link. Responses appear in the link response list.
                  </p>
                </div>
                <button
                  className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  onClick={handleAddForm}
                  type="button"
                >
                  Add form
                </button>
              </div>
              <div className="mt-4 grid gap-3 lg:grid-cols-3">
                <div>
                  <label className="field-label mt-0" htmlFor="quick-form-preset">Preset</label>
                  <select
                    id="quick-form-preset"
                    className="field"
                    value={formPresetId}
                    onChange={(event) => setFormPresetId(event.target.value)}
                  >
                    {quickFormPresets.map((preset) => (
                      <option key={preset.id} value={preset.id}>{preset.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label mt-0" htmlFor="quick-form-title">Title</label>
                  <input
                    id="quick-form-title"
                    className="field"
                    placeholder="Meeting form"
                    value={formTitle}
                    onChange={(event) => setFormTitle(event.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label mt-0" htmlFor="quick-form-description">Description</label>
                  <input
                    id="quick-form-description"
                    className="field"
                    placeholder="Agenda, prep, minutes, decisions, and follow-ups"
                    value={formDescription}
                    onChange={(event) => setFormDescription(event.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {assetInputMode === 'link-bio' && (
            <div className="mt-5 border-t border-slate-200 pt-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-950">Profile Cards</p>
                  <p className="mt-1 max-w-xl text-sm leading-6 text-slate-500">
                    Add one compact profile at a time. Each card flips for details and links to the full profile.
                  </p>
                </div>
                <button
                  className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!isValidUrl(linkBioUrl)}
                  onClick={handleAddLinkBio}
                  type="button"
                >
                  Add profile card
                </button>
              </div>
              <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,16rem)_1fr]">
                <div>
                  <label className="field-label mt-0" htmlFor="link-bio-title">Profile title</label>
                  <input
                    id="link-bio-title"
                    className="field"
                    placeholder="Mfundo Dzani"
                    value={linkBioTitle}
                    onChange={(event) => setLinkBioTitle(event.target.value)}
                  />
                  <label className="field-label" htmlFor="link-bio-tagline">Tagline</label>
                  <input
                    id="link-bio-tagline"
                    className="field"
                    placeholder="Certified electrician serving Durban"
                    value={linkBioTagline}
                    onChange={(event) => setLinkBioTagline(event.target.value)}
                  />
                  <label className="field-label" htmlFor="link-bio-description">Profile summary</label>
                  <textarea
                    id="link-bio-description"
                    className="field min-h-24"
                    placeholder="Short summary, offer, service area, or profile note"
                    value={linkBioDescription}
                    onChange={(event) => setLinkBioDescription(event.target.value)}
                  />
                  <label className="field-label" htmlFor="link-bio-experience">Experience</label>
                  <textarea
                    id="link-bio-experience"
                    className="field min-h-20"
                    placeholder="10 years supporting residential and commercial clients"
                    value={linkBioExperience}
                    onChange={(event) => setLinkBioExperience(event.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label mt-0" htmlFor="link-bio-url">Main profile URL</label>
                  <input
                    id="link-bio-url"
                    className="field"
                    placeholder="https://example.com/profile"
                    value={linkBioUrl}
                    onChange={(event) => setLinkBioUrl(event.target.value)}
                  />
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="field-label mt-0" htmlFor="link-bio-cta">Primary action</label>
                      <input
                        id="link-bio-cta"
                        className="field"
                        placeholder="Open profile"
                        value={linkBioCtaLabel}
                        onChange={(event) => setLinkBioCtaLabel(event.target.value)}
                      />
                    </div>
                    <div>
                      <label className="field-label mt-0" htmlFor="link-bio-section">Profile type</label>
                      <input
                        id="link-bio-section"
                        className="field"
                        placeholder="Personal, Product, Service, or Organisation"
                        value={linkBioSection}
                        onChange={(event) => setLinkBioSection(event.target.value)}
                      />
                    </div>
                  </div>
                  <label className="field-label" htmlFor="link-bio-icon">Profile image or logo</label>
                  <label className="upload-tile aspect-[5/2]">
                    {linkBioIconPreview ? (
                      <img src={linkBioIconPreview} alt="Link icon preview" />
                    ) : (
                      <span>Upload image</span>
                    )}
                    <input
                      id="link-bio-icon"
                      type="file"
                      accept="image/*"
                      onChange={async (event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (!file) return;
                        try {
                          const optimisedFile = await optimiseImageFile(file);
                          setLinkBioIconFile(optimisedFile);
                          setLinkBioIconPreview(URL.createObjectURL(optimisedFile));
                        } catch (error) {
                          console.error('Unable to optimise profile image.', error);
                          setLinkBioIconFile(null);
                          setLinkBioIconPreview('');
                        }
                      }}
                    />
                  </label>
                  <label className="mt-3 flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700" htmlFor="link-bio-primary">
                    <input
                      id="link-bio-primary"
                      type="checkbox"
                      checked={linkBioIsPrimary}
                      onChange={(event) => setLinkBioIsPrimary(event.target.checked)}
                    />
                    Featured profile
                  </label>
                  <InlineTagEditor
                    inputId="link-bio-qualifications"
                    label="Qualifications / Accreditations"
                    onChange={setLinkBioQualifications}
                    placeholder="Trade certificate"
                    value={linkBioQualifications}
                  />
                  <InlineTagEditor
                    inputId="link-bio-tags"
                    label="Tags"
                    onChange={setLinkBioTags}
                    placeholder="Emergency repairs"
                    value={linkBioTags}
                  />
                </div>
              </div>
            </div>
          )}

          {assetInputMode === 'markdown' && (
            <div className="mt-5 border-t border-slate-200 pt-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-950">Rich Content</p>
                <p className="mt-1 max-w-xl text-sm leading-6 text-slate-500">
                  Add formatted content with pasted Markdown, images, video links, YouTube links, tables, and safe HTML embeds.
                </p>
              </div>
              <button
                className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!articleMarkdown.trim()}
                onClick={handleAddMarkdownArticle}
                type="button"
              >
                Add content
              </button>
            </div>
            <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,16rem)_1fr]">
              <div>
                <label className="field-label mt-0" htmlFor="markdown-article-title">Title</label>
                <input
                  id="markdown-article-title"
                  className="field"
                  placeholder="Release notes"
                  value={articleTitle}
                  onChange={(event) => setArticleTitle(event.target.value)}
                />
              </div>
              <div className="grid min-w-0 gap-3 xl:grid-cols-2">
                <div className="min-w-0">
                  <RichContentEditor
                    id="markdown-article-body"
                    label="Rich content"
                    uploadImage={uploadRichContentImage}
                    placeholder={'## Summary\n\nPaste Markdown content here.\n\n![Image alt](https://example.com/image.jpg)\n\nhttps://www.youtube.com/watch?v=VIDEO_ID\n\n<iframe src="https://example.com/embed"></iframe>'}
                    value={articleMarkdown}
                    onChange={setArticleMarkdown}
                  />
                </div>
                <div className="min-w-0">
                  <p className="field-label mt-0">Rendered preview</p>
                  <div className="markdown-article-draft-preview">
                    {articleMarkdown.trim() ? (
                      <RichContent className="p-4">{articleMarkdown}</RichContent>
                    ) : (
                      <p className="p-4 text-sm leading-6 text-slate-500">Markdown preview appears here.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="label-caps">Asset grids</p>
              <h2 className="mt-2 text-xl font-semibold tracking-normal">Organize the attached files</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                Configure each public grid independently, including its category, layout, and columns.
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-4 border-t border-slate-200 pt-5 md:grid-cols-2">
            <div>
              <p className="text-sm font-semibold text-slate-950">Public page hero</p>
              <label className="mt-3 flex items-center gap-2 text-sm font-semibold text-slate-700" htmlFor="show-hero-section">
                <input
                  id="show-hero-section"
                  type="checkbox"
                  checked={link.showHeroSection !== false}
                  onChange={(event) => onChange({ ...link, showHeroSection: event.target.checked })}
                />
                Show title and link details above the content
              </label>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-950" htmlFor="public-page-width">
                Public page width
              </label>
              <select
                id="public-page-width"
                className="field mt-3"
                value={link.publicPageWidth || '6xl'}
                onChange={(event) => onChange({ ...link, publicPageWidth: event.target.value })}
              >
                {publicPageWidthOptions.map((width) => (
                  <option key={width} value={width}>{width}</option>
                ))}
              </select>
            </div>

          </div>

          <div className="mt-5 border-t border-slate-200 pt-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-950">Content grids</p>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                  Create named grids for the public page. New uploads and manual assets are added to the active grid.
                </p>
              </div>
              <button
                className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                onClick={handleAddGrid}
                type="button"
              >
                Add grid
              </button>
            </div>

            <div className="mt-4 flex gap-2 overflow-x-auto rounded border border-slate-200 bg-slate-50 p-2">
              {assetGrids.map((grid) => (
                <button
                  aria-pressed={activeGrid?.id === grid.id}
                  className={activeGrid?.id === grid.id ? 'segmented active shrink-0' : 'segmented shrink-0'}
                  key={grid.id}
                  onClick={() => setActiveGridId(grid.id)}
                  type="button"
                >
                  {grid.title}
                </button>
              ))}
            </div>

            {activeGrid && (
              <div className="mt-4 rounded border border-slate-200 bg-white p-4">
                <div className="grid gap-3 lg:grid-cols-[1fr_minmax(10rem,14rem)_auto] lg:items-end">
                  <div>
                    <label className="field-label mt-0" htmlFor={`grid-title-${activeGrid.id}`}>Grid name</label>
                    <input
                      className="field"
                      id={`grid-title-${activeGrid.id}`}
                      value={activeGrid.title}
                      onChange={(event) => handleGridTitleChange(activeGrid.id, event.target.value)}
                    />
                  </div>
                  <div>
                    <label className="field-label mt-0" htmlFor={`grid-type-${activeGrid.id}`}>Grid type</label>
                    <select
                      className="field"
                      id={`grid-type-${activeGrid.id}`}
                      value={activeGrid.type || 'standard'}
                      onChange={(event) => handleGridTypeChange(activeGrid.id, event.target.value)}
                    >
                      {gridTypes.map((gridType) => (
                        <option key={gridType.id} value={gridType.id}>{gridType.label}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={assetGrids.length <= 1}
                    onClick={() => handleDeleteGrid(activeGrid.id)}
                    type="button"
                  >
                    Delete grid
                  </button>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="field-label mt-0" htmlFor={`grid-category-${activeGrid.id}`}>Grid category</label>
                    <select
                      className="field"
                      id={`grid-category-${activeGrid.id}`}
                      value={activeGrid.category || 'album'}
                      onChange={(event) => handleGridFieldChange(activeGrid.id, 'category', event.target.value)}
                    >
                      {gridCategories.map((gridCategory) => (
                        <option key={gridCategory.id} value={gridCategory.id}>{gridCategory.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="field-label mt-0" htmlFor={`grid-access-${activeGrid.id}`}>Grid access</label>
                    <select
                      className="field"
                      id={`grid-access-${activeGrid.id}`}
                      value={normaliseGridAccessLevel(activeGrid.accessLevel)}
                      onChange={(event) => handleGridFieldChange(activeGrid.id, 'accessLevel', event.target.value)}
                    >
                      {gridAccessLevels.map((accessLevel) => (
                        <option key={accessLevel.id} value={accessLevel.id}>{accessLevel.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {!['list', 'rich-content'].includes(activeGrid.type) && (
                <div className="mt-4 grid gap-3 border-t border-slate-200 pt-4 sm:grid-cols-[1fr_8rem]">
                  {activeGrid.category === 'listing' && (
                    <div className="sm:col-span-2">
                      <label className="field-label mt-0" htmlFor={`grid-listing-type-${activeGrid.id}`}>Listing type</label>
                      <select
                        className="field"
                        id={`grid-listing-type-${activeGrid.id}`}
                        value={activeGrid.listingType || defaultListingType}
                        onChange={(event) => handleGridFieldChange(activeGrid.id, 'listingType', event.target.value)}
                      >
                        {listingTypes.map((listingType) => (
                          <option key={listingType.id} value={listingType.id}>{listingType.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <p className="field-label mt-0">Grid layout</p>
                    <div className="grid grid-cols-2 gap-2 rounded border border-slate-200 bg-slate-50 p-1">
                      <button
                        className={activeGrid.layout === 'grid' ? 'segmented active' : 'segmented'}
                        onClick={() => handleGridFieldChange(activeGrid.id, 'layout', 'grid')}
                        type="button"
                      >
                        Grid
                      </button>
                      <button
                        className={activeGrid.layout !== 'grid' ? 'segmented active' : 'segmented'}
                        onClick={() => handleGridFieldChange(activeGrid.id, 'layout', 'masonry')}
                        type="button"
                      >
                        Masonry
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="field-label mt-0" htmlFor={`grid-columns-${activeGrid.id}`}>Columns</label>
                    <input
                      id={`grid-columns-${activeGrid.id}`}
                      className="field"
                      max="4"
                      min="1"
                      type="number"
                      value={activeGridColumns}
                      onChange={(event) => handleGridFieldChange(
                        activeGrid.id,
                        'columns',
                        Math.min(Math.max(Number(event.target.value) || 1, 1), 4),
                      )}
                    />
                  </div>
                </div>
                )}

                {activeGrid.type === 'rich-content' && (
                  <div className="mt-4 border-t border-slate-200 pt-4">
                    <div className="grid gap-4">
                      <div>
                        <label className="field-label mt-0" htmlFor={`grid-rich-content-title-${activeGrid.id}`}>Content title</label>
                        <input
                          className="field"
                          id={`grid-rich-content-title-${activeGrid.id}`}
                          placeholder="Release notes"
                          value={activeGrid.richContentTitle || ''}
                          onChange={(event) => handleGridFieldChange(activeGrid.id, 'richContentTitle', event.target.value)}
                        />
                      </div>
                      <div className="min-w-0">
                        <RichContentEditor
                          id={`grid-rich-content-markdown-${activeGrid.id}`}
                          label="Rich content"
                          uploadImage={uploadRichContentImage}
                          placeholder={'## Summary\n\nPaste Markdown content here.\n\n![Image alt](https://example.com/image.jpg)\n\nhttps://www.youtube.com/watch?v=VIDEO_ID\n\n<iframe src="https://example.com/embed"></iframe>'}
                          value={activeGrid.richContentMarkdown || ''}
                          onChange={(value) => handleGridFieldChange(activeGrid.id, 'richContentMarkdown', value)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeGrid.type === 'list' && (
                  <div className="mt-4 border-t border-slate-200 pt-4">
                    <div className="grid gap-4">
                      <div className="grid gap-3 rounded border border-slate-200 bg-slate-50 p-3 lg:grid-cols-2">
                        <div>
                          <label className="field-label mt-0" htmlFor={`grid-list-title-${activeGrid.id}`}>List title</label>
                          <input
                            className="field"
                            id={`grid-list-title-${activeGrid.id}`}
                            placeholder="Grade 10 class list"
                            value={activeGrid.listTitle || ''}
                            onChange={(event) => handleGridFieldChange(activeGrid.id, 'listTitle', event.target.value)}
                          />
                        </div>
                        <div>
                          <label className="field-label mt-0" htmlFor={`grid-list-image-${activeGrid.id}`}>Image URL</label>
                          <input
                            className="field"
                            id={`grid-list-image-${activeGrid.id}`}
                            placeholder="https://example.com/image.jpg"
                            value={activeGrid.listImageUrl || ''}
                            onChange={(event) => handleGridFieldChange(activeGrid.id, 'listImageUrl', event.target.value)}
                          />
                        </div>
                        <div className="lg:col-span-2">
                          <label className="field-label" htmlFor={`grid-list-description-${activeGrid.id}`}>Description</label>
                          <textarea
                            className="field min-h-24"
                            id={`grid-list-description-${activeGrid.id}`}
                            placeholder="Shared context for this list"
                            value={activeGrid.listDescription || ''}
                            onChange={(event) => handleGridFieldChange(activeGrid.id, 'listDescription', event.target.value)}
                          />
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 lg:col-span-2">
                          <div>
                            <label className="field-label mt-0" htmlFor={`grid-list-action-label-${activeGrid.id}`}>List action</label>
                            <input
                              className="field"
                              id={`grid-list-action-label-${activeGrid.id}`}
                              placeholder="Open register"
                              value={activeGrid.listActionLabel || ''}
                              onChange={(event) => handleGridFieldChange(activeGrid.id, 'listActionLabel', event.target.value)}
                            />
                          </div>
                          <div>
                            <label className="field-label mt-0" htmlFor={`grid-list-action-url-${activeGrid.id}`}>Action URL</label>
                            <input
                              className="field"
                              id={`grid-list-action-url-${activeGrid.id}`}
                              placeholder="https://example.com"
                              value={activeGrid.listActionUrl || ''}
                              onChange={(event) => handleGridFieldChange(activeGrid.id, 'listActionUrl', event.target.value)}
                            />
                          </div>
                        </div>
                        <div className="lg:col-span-2">
                          <InlineTagEditor
                            inputId={`grid-list-tags-${activeGrid.id}`}
                            label="List tags"
                            onChange={(nextTags) => handleGridFieldChange(activeGrid.id, 'listTags', nextTags)}
                            placeholder="Term 2"
                            value={activeGrid.listTags || []}
                          />
                        </div>
                      </div>

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-slate-950">Rows and columns</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                              onClick={() => handleAddListColumn(activeGrid.id)}
                              type="button"
                            >
                              Add column
                            </button>
                            <button
                              className="rounded bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                              onClick={() => handleAddListRow(activeGrid.id)}
                              type="button"
                            >
                              Add row
                            </button>
                          </div>
                        </div>
                        {(listUploadStatus || listUploadError) && (
                          <p className={`mt-3 text-sm ${listUploadError ? 'text-red-600' : 'text-slate-500'}`}>
                            {listUploadError || listUploadStatus}
                          </p>
                        )}
                        <div className="mt-3 overflow-x-auto rounded border border-slate-200">
                          <table className="list-editor-table">
                            <thead>
                              <tr>
                                {(activeGrid.listColumns || []).map((column) => (
                                  <th key={column.id}>
                                    <div className="grid min-w-56 gap-2">
                                      <input
                                        className="field"
                                        value={column.label}
                                        onChange={(event) => updateGridList(activeGrid.id, (grid) => ({
                                          ...grid,
                                          listColumns: (grid.listColumns || []).map((currentColumn) => (
                                            currentColumn.id === column.id
                                              ? { ...currentColumn, label: event.target.value }
                                              : currentColumn
                                          )),
                                        }))}
                                      />
                                      <div className="flex gap-2">
                                        <select
                                          className="field"
                                          value={normaliseListColumnType(column.type)}
                                          onChange={(event) => handleListColumnTypeChange(activeGrid.id, column.id, event.target.value)}
                                        >
                                          {listColumnTypes.map((columnType) => (
                                            <option key={columnType.id} value={columnType.id}>{columnType.label}</option>
                                          ))}
                                        </select>
                                      {normaliseListColumnType(column.type) === 'amount' && (
                                        <select
                                          aria-label={`${column.label} currency symbol`}
                                          className="field w-16 shrink-0 px-2"
                                            value={normaliseAmountCurrencySymbol(column.currencySymbol)}
                                            onChange={(event) => handleListColumnFieldChange(
                                              activeGrid.id,
                                              column.id,
                                              'currencySymbol',
                                              event.target.value,
                                            )}
                                          >
                                            {amountCurrencySymbols.map((currencySymbol) => (
                                              <option key={currencySymbol.id} value={currencySymbol.id}>{currencySymbol.label}</option>
                                          ))}
                                        </select>
                                      )}
                                      {normaliseListColumnType(column.type) === 'interaction' && (
                                        <div className="grid gap-2">
                                          <select
                                            aria-label={`${column.label} interaction type`}
                                            className="field"
                                            value={normaliseInteractionType(column.interaction?.type)}
                                            onChange={(event) => handleListColumnFieldChange(
                                              activeGrid.id,
                                              column.id,
                                              'interaction',
                                              {
                                                ...(column.interaction || {}),
                                                type: event.target.value,
                                                options: column.interaction?.options || [],
                                              },
                                            )}
                                          >
                                            {interactionTypes.map((interactionType) => (
                                              <option key={interactionType.id} value={interactionType.id}>{interactionType.label}</option>
                                            ))}
                                          </select>
                                          {['buttons', 'select'].includes(normaliseInteractionType(column.interaction?.type)) && (
                                            <InteractionOptionsInput
                                              column={column}
                                              key={`${column.id}-${normaliseInteractionType(column.interaction?.type)}`}
                                              onCommit={(options) => handleListColumnFieldChange(
                                                activeGrid.id,
                                                column.id,
                                                'interaction',
                                                {
                                                  ...(column.interaction || {}),
                                                  type: normaliseInteractionType(column.interaction?.type),
                                                  options,
                                                },
                                              )}
                                            />
                                          )}
                                        </div>
                                      )}
                                      {normaliseListColumnType(column.type) === 'formula' && (
                                          <button
                                            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                                            onClick={() => openFormulaModal(column)}
                                            type="button"
                                          >
                                            Formula
                                          </button>
                                        )}
                                        <button
                                          aria-label="Remove column"
                                          className="rounded border border-red-200 bg-red-50 px-2 text-sm font-semibold text-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                                          disabled={(activeGrid.listColumns || []).length <= 1}
                                          onClick={() => handleRemoveListColumn(activeGrid.id, column.id)}
                                          type="button"
                                        >
                                          x
                                        </button>
                                      </div>
                                    </div>
                                  </th>
                                ))}
                                <th>Action</th>
                                <th aria-label="Row controls" />
                              </tr>
                            </thead>
                            <tbody>
                              {(activeGrid.listRows || []).map((row) => (
                                <tr key={row.id}>
                                  {(activeGrid.listColumns || []).map((column) => (
                                    <td key={column.id}>
                                      {renderListEditorCell(row, column)}
                                    </td>
                                  ))}
                                  <td>
                                    <div className="grid min-w-64 gap-2">
                                      <input
                                        className="field"
                                        placeholder="Action label"
                                        value={row.actionLabel || ''}
                                        onChange={(event) => handleListRowFieldChange(activeGrid.id, row.id, 'actionLabel', event.target.value)}
                                      />
                                      <input
                                        className="field"
                                        placeholder="https://..."
                                        value={row.actionUrl || ''}
                                        onChange={(event) => handleListRowFieldChange(activeGrid.id, row.id, 'actionUrl', event.target.value)}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <button
                                      className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700"
                                      onClick={() => handleRemoveListRow(activeGrid.id, row.id)}
                                      type="button"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              {!activeGrid.listRows?.length && (
                                <tr>
                                  <td className="px-3 py-4 text-sm text-slate-500" colSpan={(activeGrid.listColumns || []).length + 2}>
                                    Add a row to start the list.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!['list', 'rich-content'].includes(activeGrid.type) && (
                <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {(link.assets || []).map((asset) => (
                    <label
                      className="flex min-w-0 items-center gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700"
                      htmlFor={`grid-${activeGrid.id}-asset-${asset.id}`}
                      key={asset.id}
                    >
                      <input
                        className="shrink-0"
                        checked={(activeGrid.assetIds || []).includes(asset.id)}
                        id={`grid-${activeGrid.id}-asset-${asset.id}`}
                        onChange={(event) => handleGridAssetToggle(activeGrid.id, asset.id, event.target.checked)}
                        type="checkbox"
                      />
                      <GridAssetThumbnail asset={asset} />
                      <span className="min-w-0">
                        <span className="block truncate">{asset.title || asset.name || 'Untitled asset'}</span>
                        <span className="mt-0.5 block truncate text-xs font-normal text-slate-500">
                          {[asset.type, asset.size].filter(Boolean).join(' / ') || 'Asset'}
                        </span>
                      </span>
                    </label>
                  ))}
                  {!link.assets?.length && (
                    <p className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                      Add assets before assigning grid content.
                    </p>
                  )}
                </div>
                )}
              </div>
            )}
          </div>

          {!['list', 'rich-content'].includes(activeGrid?.type) && (
          <div className="mt-5 border-t border-slate-200 pt-5">
            <p className="text-sm font-semibold text-slate-950">Asset fields</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {assetFieldOptions.map((field) => (
                <label
                  className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700"
                  htmlFor={`asset-field-${field.id}`}
                  key={field.id}
                >
                  <input
                    id={`asset-field-${field.id}`}
                    type="checkbox"
                    checked={Boolean(assetFields[field.id])}
                    onChange={(event) => onChange({
                      ...link,
                      assetFields: {
                        ...assetFields,
                        [field.id]: event.target.checked,
                      },
                    })}
                  />
                  {field.label}
                </label>
              ))}
            </div>
          </div>
          )}
        </section>

        {activeGrid?.type === 'list' ? (
          <ShareListTable grid={activeGrid} />
        ) : activeGrid?.type === 'rich-content' ? (
          <section className="rich-content-grid-renderer">
            {activeGrid.richContentTitle && (
              <h2>{activeGrid.richContentTitle}</h2>
            )}
            {(activeGrid.richContentMarkdown || '').trim() ? (
              <RichContent>{activeGrid.richContentMarkdown}</RichContent>
            ) : (
              <p className="text-sm leading-6 text-slate-500">Add rich content to this grid to preview it here.</p>
            )}
          </section>
        ) : (
          <AssetGallery
            assets={activeGridAssets}
            assetFields={assetFields}
            editable
            enableHtmlEmbedFullscreen={activeGrid?.type === 'presentation'}
            layout={activeGrid?.layout || link.layout}
            columns={activeGridColumns}
            link={link}
            listingType={activeGrid?.category === 'listing' ? activeGrid?.listingType || defaultListingType : ''}
            cartButtonLabel={checkout.cartButtonLabel}
            onAssetChange={onAssetChange}
            onAssetDelete={onAssetDelete}
            onAssetMove={handleActiveGridAssetMove}
            showStoreQuantityHint={checkoutGridCategories.includes(activeGrid?.category)}
          />
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            className="rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
            disabled={!isSignedIn || isSaving || isUploadingAssets}
            onClick={handleSaveClick}
            type="button"
          >
            {isSaving ? 'Saving...' : link.id ? 'Save assets' : 'Create link and save assets'}
          </button>
          <button
            className="rounded border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
            onClick={onOpenLiveShare}
            type="button"
          >
            Open live share page
          </button>
        </div>
      </section>
      <FormulaModal
        column={activeFormulaColumn}
        columns={activeGrid?.listColumns || []}
        draft={formulaDraft}
        onClose={() => {
          setFormulaColumnId('');
          setFormulaDraft({ preset: 'sum', columnIds: [] });
        }}
        onDraftChange={setFormulaDraft}
        onSave={saveFormulaColumn}
      />
    </section>
  );
}

export default LinkCreationPage;
